import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import * as d3 from 'd3'
import { ref, toRefs, watch } from 'vue'
import { useComponent } from './useComponent'
import { FormatOptions, formatter } from '@/components/AnalyticsDashboard/formatter'
import { useI18n } from 'vue-i18n'
import { Pattern } from '../chart/bars'
import { addPattern } from '../utils'

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'

export type TooltipProps = {
  xLimits: [number, number]
  yLimits: [number, number]
  yFormat: FormatOptions
  xFormat?: FormatOptions
  position?: TooltipPosition
}

type TooltipRow = {
  label: string
  color: string
  value: number
  pattern?: Pattern
  dataXValue: number | string
}

const TOOLTIP_ID = '#chart-tooltip'
const TOOLTIP_OFFSET = 10


export default /*@__PURE__*/_defineComponent({
  __name: 'TooltipComponent',
  props: {
    xLimits: {},
    yLimits: {},
    yFormat: {},
    xFormat: { default: undefined },
    position: { default: 'top' }
  },
  setup(__props: any) {

const props = __props

const { locale } = useI18n()

const { xLimits, yLimits, yFormat, xFormat, position } = toRefs(props)

const group = ref<SVGGElement | null>(null)

let currentSvgNode: SVGElement | null = null
let handleMouseMove: ((event: Event) => void) | null = null
let handleMouseLeave: (() => void) | null = null

useComponent(group, ({ width, height, parentGroup, svg, padding }) => {
  const defs = svg.select<SVGDefsElement>('defs')

  const xScale = d3.scaleLinear().range([0, width])
  const yScale = d3.scaleLinear().range([height, 0])

  const updateScales = () => {
    xScale.domain(xLimits.value)
    yScale.domain(yLimits.value)
  }

  watch(
    [xLimits, yLimits, () => width, () => height],
    () => {
      xScale.range([0, width])
      yScale.range([height, 0])
      updateScales()
    },
    { immediate: true }
  )

  const tooltip = d3.select<HTMLElement, unknown>(TOOLTIP_ID)
  if (tooltip.empty()) {
    console.error(`${TOOLTIP_ID} element not found! Tooltip will not function.`)
  }

  currentSvgNode = svg.node()
  if (!currentSvgNode) return

  let prevX = NaN
  let prevY = NaN

  handleMouseMove = () => {
    const tooltipNode = d3.select<HTMLElement, unknown>(TOOLTIP_ID)
    if (tooltipNode.empty() || !svg.node()) return

    tooltipNode.style('display', 'block')

    const highlights = parentGroup.selectAll('.highlight')
    if (highlights.empty()) {
      tooltipNode.style('display', 'none')
      return
    }

    const data = [...highlights]
      .map((x) => d3.select(x))
      .map((highlight) => {
        const tooltipX = parseFloat(highlight.attr('data-tooltip-x') || '0')
        const tooltipY = parseFloat(highlight.attr('data-tooltip-y') || '0')
        const dataY = parseFloat(highlight.attr('data-y') || '0')
        const dataXValueAttr = highlight.attr('data-value-x') || ''
        const dataXValue: string | number = /^\d+(\.\d+)?$/.test(dataXValueAttr)
          ? parseFloat(dataXValueAttr)
          : dataXValueAttr
        const color = highlight.attr('data-color') || 'black'
        const label = highlight.attr('data-label') || ''
        let pattern: Pattern | undefined = undefined
        try {
          pattern = JSON.parse(highlight.attr('data-pattern') || 'null') || undefined
        } catch (e) {
          /* ignore */
        }

        const x = xScale(tooltipX)
        const y = yScale(tooltipY)

        return { value: dataY, color, label, pattern, x, y, dataXValue }
      })
      .filter((item) => !isNaN(item.x) && !isNaN(item.y))
      .sort((a, b) => b.value - a.value)

    if (data.length === 0) {
      tooltipNode.style('display', 'none')
      return
    }

    const anchorX = data[0]?.x

    const avgScreenY = data.reduce((acc, { y }) => acc + y, 0) / data.length

    if (anchorX === undefined || isNaN(anchorX) || isNaN(avgScreenY)) {
      tooltipNode.style('display', 'none')
      return
    }

    if (Math.abs(anchorX - prevX) < 0.1 && Math.abs(avgScreenY - prevY) < 0.1) {
      return // No significant movement
    }

    prevX = anchorX
    prevY = avgScreenY

    const tooltipRowsData: TooltipRow[] = data.map(
      ({ color, label, pattern, value, dataXValue }) => ({
        color,
        label,
        pattern,
        value,
        dataXValue
      })
    )

    const html = generateTooltipContent(
      tooltipRowsData,
      tooltipNode.node() as HTMLElement,
      yFormat.value,
      defs as unknown as d3.Selection<d3.BaseType, unknown, null, undefined>,
      xFormat?.value
    )
    tooltipNode.select('.content').html(html)

    const svgBounds = svg.node()?.getBoundingClientRect()
    const tooltipBounds = tooltipNode.node()?.getBoundingClientRect()

    if (!svgBounds || !tooltipBounds) {
      tooltipNode.style('display', 'none')
      return
    }

    const svgScrollX = window.scrollX ?? window.pageXOffset
    const svgScrollY = window.scrollY ?? window.pageYOffset

    const baseX = svgBounds.left + svgScrollX + padding.left + anchorX
    const baseY = svgBounds.top + svgScrollY + padding.top + avgScreenY

    let finalX = baseX
    let finalY = baseY
    let currentPosition = position.value

    const tooltipWidth = tooltipBounds.width
    const tooltipHeight = tooltipBounds.height

    let xOffset = 0
    let yOffset = 0

    if (currentPosition === 'left' || currentPosition === 'right') {
      yOffset = -tooltipHeight / 2

      if (currentPosition === 'left') {
        xOffset = -tooltipWidth - TOOLTIP_OFFSET

        if (baseX + xOffset < svgScrollX) {
          currentPosition = 'right'
          xOffset = TOOLTIP_OFFSET
        }
      } else {
        xOffset = TOOLTIP_OFFSET

        if (baseX + xOffset + tooltipWidth > window.innerWidth + svgScrollX) {
          currentPosition = 'left'
          xOffset = -tooltipWidth - TOOLTIP_OFFSET
        }
      }
    } else {
      xOffset = -tooltipWidth / 2

      if (currentPosition === 'top') {
        yOffset = -tooltipHeight - TOOLTIP_OFFSET

        if (baseY + yOffset < svgScrollY) {
          currentPosition = 'bottom'
          yOffset = TOOLTIP_OFFSET
        }
      } else {
        yOffset = TOOLTIP_OFFSET

        if (baseY + yOffset + tooltipHeight > window.innerHeight + svgScrollY) {
          currentPosition = 'top'
          yOffset = -tooltipHeight - TOOLTIP_OFFSET
        }
      }
    }

    finalX = baseX + xOffset
    finalY = baseY + yOffset

    tooltipNode
      .style('transform', `translate(${finalX}px, ${finalY}px)`)
      .classed('top', currentPosition === 'top')
      .classed('bottom', currentPosition === 'bottom')
      .classed('left', currentPosition === 'left')
      .classed('right', currentPosition === 'right')
      .classed('after-init', true)
  }

  handleMouseLeave = () => {
    prevX = NaN
    prevY = NaN
    const tooltipNode = d3.select<HTMLElement, unknown>(TOOLTIP_ID)
    if (!tooltipNode.empty()) {
      tooltipNode.style('display', 'none')

      tooltipNode.classed('top bottom left right', false)
    }
  }

  document.addEventListener('wheel', handleMouseLeave)
  currentSvgNode.addEventListener('mousemove', handleMouseMove)
  currentSvgNode.addEventListener('mouseenter', handleMouseMove)
  currentSvgNode.addEventListener('mouseleave', handleMouseLeave)

  return {
    destroy: () => {
      if (currentSvgNode) {
        if (handleMouseMove) {
          currentSvgNode.removeEventListener('mousemove', handleMouseMove)
          currentSvgNode.removeEventListener('mouseenter', handleMouseMove)
        }
        if (handleMouseLeave) {
          currentSvgNode.removeEventListener('mouseleave', handleMouseLeave)
          document.removeEventListener('wheel', handleMouseLeave)
        }
      }

      currentSvgNode = null
      handleMouseMove = null
      handleMouseLeave = null

      d3.select(TOOLTIP_ID).style('display', 'none')
    }
  }
})

function generateTooltipContent(
  rows: TooltipRow[],
  tooltipEl: HTMLElement | null,
  yFormat: FormatOptions,
  defs: d3.Selection<d3.BaseType, unknown, null, undefined>,
  xFormat?: FormatOptions
): string {
  if (!tooltipEl || rows.length === 0) return ''

  const tooltip = d3.select(tooltipEl)
  const rowYtemplate = tooltip.select<HTMLElement>('#row-y')
  const rowX = tooltip.select<HTMLElement>('#row-x')

  if (rowYtemplate.empty() || rowX.empty()) {
    console.error('Tooltip template elements #row-y or #row-x not found within', tooltipEl)
    return ''
  }

  const groupedRows = new Map<string, TooltipRow[]>()
  rows.forEach((row) => {
    const key = `${row.dataXValue}-${row.value}`
    if (!groupedRows.has(key)) {
      groupedRows.set(key, [])
    }
    groupedRows.get(key)?.push(row)
  })

  const firstRow = rows[0]
  const xValue = firstRow.dataXValue
  const xDataText = typeof xValue === 'number' ? formatter(xValue, locale.value, xFormat) : xValue

  const xColInfo = rowX.select('.col .info .text')
  if (xFormat) {
    rowX.style('display', 'block')
    xColInfo.text(xDataText).style('display', 'block')
  } else {
    // xColInfo.text('').style('display', 'none')
    rowX.style('display', 'none')
  }

  const rowXNode = rowX.node()
  const headerHTML = rowXNode ? rowXNode.outerHTML : ''

  const bodyHTML = Array.from(groupedRows.values())
    .map((group) => {
      if (!group || group.length === 0) return ''

      const representativeRow = group[0]
      const { color, pattern, value } = representativeRow

      const combinedLabel = group.map((r) => r.label).join('<br/>')

      const rowY = rowYtemplate.clone(true)

      rowY
        .select('.col .tooltip-legend .background-rect')
        .attr('fill', pattern?.background ? pattern.background : !pattern ? color : 'transparent')

      const patternId = pattern ? addPattern(pattern, defs) : undefined
      rowY
        .select('.col .tooltip-legend .foreground-rect')
        .attr('fill', patternId ? `url(#${patternId})` : 'none')

      rowY.select('.col .row .label .text').html(combinedLabel)
      rowY.select('.col .row .value .text').text(formatter(value, locale.value, yFormat))

      if (!combinedLabel) {
        rowY.select('.col .row .label').style('display', 'none')
      } else {
        rowY.select('.col .row .label').style('display', 'block')
      }

      const rowNode = rowY.node()
      return rowNode ? rowNode.outerHTML : ''
    })
    .join('')

  return headerHTML + bodyHTML
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("g", {
    ref_key: "group",
    ref: group
  }, null, 512))
}
}

})